"use client"

import { profileHref } from "@/lib/profile-url"
import { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Folder, Globe, Lock, ChevronLeft, ChevronRight, MoreHorizontal, Edit, Trash2, ArrowRight, Star, Download, Loader2, Users } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { downloadGoonfolio } from "@/lib/goonfolio-download"
import type { CollectionItemDetail, CollectionListItem } from "./types"
import { useTranslations } from "next-intl"

interface CollectionCardProps {
  collection: CollectionListItem
  isOwner: boolean
  onEdit?: (collection: CollectionListItem) => void
  onDelete?: (id: string) => void
  onRate?: (collection: CollectionListItem) => void
}

// Same card shell/layout as components/commissions/artist-card.tsx (ambient
// blur, aspect-video hero with a hover carousel + thumbnail strip, name +
// status row, avatar/username footer) — just swapped price/rating for
// item-count/visibility since a folio isn't a storefront listing.
export function CollectionCard({ collection, isOwner, onEdit, onDelete, onRate }: CollectionCardProps) {
  const t = useTranslations("collectionCard")
  const td = useTranslations("contentDownload")
  const tc = useTranslations("communityFolios")
  const router = useRouter()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    const toastId = toast.loading(t("preparingDownload"))
    try {
      const res = await fetch(`/api/collections/${collection.id}`)
      if (!res.ok) throw new Error("fetch failed")
      const data = await res.json()
      const items: CollectionItemDetail[] = data.items || []
      if (items.length === 0) {
        toast.error(t("thisFolioIsEmpty"), { id: toastId })
        return
      }
      await downloadGoonfolio(items, collection.name, undefined, td)
      toast.dismiss(toastId)
    } catch {
      toast.error(t("downloadFailedPleaseTryAgain"), { id: toastId })
    } finally {
      setDownloading(false)
    }
  }
  const thumbs = collection.previewThumbnails.length > 0
    ? collection.previewThumbnails
    : (collection.coverImageUrl ? [collection.coverImageUrl] : [])
  const current = thumbs[currentIndex] ?? null

  const ambientUrl = current ?? undefined
  const href = `/goonfolio/${collection.slug ?? collection.id}`
  const showAverageRating = collection.ratingCount >= 3 && collection.averageRating != null

  const prev = (e: React.MouseEvent) => {
    e.preventDefault()
    setCurrentIndex((i) => (i - 1 + thumbs.length) % thumbs.length)
  }
  const next = (e: React.MouseEvent) => {
    e.preventDefault()
    setCurrentIndex((i) => (i + 1) % thumbs.length)
  }

  return (
    <Link href={href} className="block">
      <div className="relative rounded-xl overflow-hidden cursor-pointer border transition-all hover:scale-[1.01] border-border/50 hover:border-accent/60 bg-card">

        {/* Ambient blur */}
        {ambientUrl && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none transition-all duration-500"
            style={{
              backgroundImage: `url(${ambientUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(24px) saturate(1.4)",
              opacity: 0.18,
              transform: "scale(1.1)",
            }}
          />
        )}

        {/* Solid backdrop over the text/footer area — the ambient blur above
            spans the whole card (inset-0), and without this the blurred cover
            thumbnail's colors (often warm skin tones) visibly tint the name,
            tags, and creator/Rate row, hurting legibility. */}
        <div className="relative z-10 p-3 space-y-3 bg-card">
          {/* Hero */}
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted/60 group">
            {!current ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground/30">
                <Folder className="h-10 w-10" />
                <span className="text-xs">{t("emptyFolio")}</span>
              </div>
            ) : (
              <img
                src={current}
                alt={collection.name}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
              />
            )}

            {/* Arrows + thumbnail strip */}
            {thumbs.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/75"
                >
                  <ChevronLeft className="h-4 w-4 text-white" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/75"
                >
                  <ChevronRight className="h-4 w-4 text-white" />
                </button>

                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {thumbs.map((url, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.preventDefault(); setCurrentIndex(i) }}
                      className={cn(
                        "w-8 h-8 rounded overflow-hidden border-2 transition-all shrink-0 bg-muted",
                        i === currentIndex
                          ? "border-white scale-110 shadow-lg"
                          : "border-white/30 opacity-60 hover:opacity-90 hover:border-white/60"
                      )}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Item count badge — same slot/style as ArtistCard's price badge */}
            <div className="absolute bottom-2 left-2 z-10 px-2 py-0.5 rounded-full text-xs font-bold bg-background/85 backdrop-blur-sm text-foreground shadow flex items-center gap-1">
              {collection.itemCount} {collection.itemCount === 1 ? "item" : "items"}
            </div>

            {/* Owner actions overlay */}
            {isOwner && (
              <div className="absolute top-2 right-2 z-20" onClick={(e) => e.preventDefault()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-sm text-white hover:text-white">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuPortal>
                    {/* Portalled — unlike the shared default, this card's
                        hover:scale-[1.01] transform on the outer wrapper makes
                        it the containing block for the menu's position:fixed
                        content when rendered inline, so the menu's position
                        re-solves continuously against that animating scale
                        and visibly flickers while the card's hover transition
                        runs. Escaping to document.body sidesteps it — this
                        card isn't a bounded widget, so there's no collision-
                        detection downside like the shared comment warns about. */}
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit?.(collection)}>
                        <Edit className="h-4 w-4 mr-2" />
                        {t("edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownload} disabled={downloading}>
                        {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                        {downloading ? t("downloading") : t("download")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => onDelete?.(collection.id)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Name + visibility row */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm leading-snug line-clamp-1 flex-1">
              {collection.name}
            </h3>
            <div className="flex items-center gap-1 shrink-0 text-xs text-muted-foreground">
              {collection.ratingCount > 0 && (
                <span className="inline-flex items-center gap-0.5 rounded-full border bg-background/70 px-1.5 py-0.5 text-[10px] font-medium">
                  <Star className="h-2.5 w-2.5 fill-amber-600 text-amber-600 dark:fill-amber-400 dark:text-amber-400" />
                  {showAverageRating ? collection.averageRating?.toFixed(1) : collection.ratingCount}
                </span>
              )}
              {collection.kind === "community" ? (
                <><Users className="h-3 w-3" />{tc("community")}</>
              ) : collection.is_public ? (
                <><Globe className="h-3 w-3" />{t("public")}</>
              ) : (
                <><Lock className="h-3 w-3" />{t("private")}</>
              )}
            </div>
          </div>

          {/* Description — always shown, independent of whether creator info is present */}
          {collection.description && (
            <p className="text-xs text-muted-foreground line-clamp-1 -mt-1">{collection.description}</p>
          )}

          {collection.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 -mt-1">
              {collection.tags.slice(0, 4).map((tag) => (
                <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Footer — a hairline divider instead of relying on the shared bg-card
              alone to separate it, so a sparse card (no description/tags) doesn't
              read as an unintentional grey box floating around just this row. */}
          <div className="flex items-center justify-between gap-2 pt-2 mt-0.5 border-t border-border/40">
            <div className="flex items-center gap-1.5 min-w-0">
              {collection.creator ? (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); router.push(profileHref(collection.creator?.short_code, collection.creator?.username, collection.user_id)) }}
                  className="flex items-center gap-1.5 min-w-0 hover:opacity-80 transition-opacity"
                  title={`View ${collection.creator.username || "creator"}'s profile`}
                >
                  <Avatar className="h-5 w-5 shrink-0">
                    <AvatarImage src={collection.creator.profile_picture_url || undefined} />
                    <AvatarFallback className="text-[9px]">
                      {collection.creator.username?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-muted-foreground truncate">@{collection.creator.username || "unknown"}</span>
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!isOwner && collection.is_public && onRate && (
                <button
                  onClick={(e) => { e.preventDefault(); onRate(collection) }}
                  className="flex items-center gap-1 text-xs font-medium text-accent-foreground/70 hover:text-accent-foreground transition-colors"
                  title={t("rateThisFolio")}
                >
                  <Star className="h-3 w-3" />
                  {t("rate")}
                </button>
              )}
              <div className="flex items-center gap-1 text-xs font-medium text-accent-foreground/70 hover:text-accent-foreground transition-colors">
                <ArrowRight className="h-3 w-3" />
                {t("view")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}
